import Http from '@/services/http';
import {
  Rules,
  AlarmLevelParam,
  SampleLookup,
  SampleSummary,
} from 'sigma-mappings';
import msis127ErrorHandler from '@/domain/data/installation/engineering/cromaMSIS127ErrorHandler';
import * as ruleResults from './ruleResults';

const urls = {
  sampleLookup: assetModuleId => `/engenharia/${assetModuleId}/cromatografia/amostras/lookup`,
  sampleSummary: (assetModuleId, sampleId) => `/engenharia/${
    assetModuleId}/cromatografia/amostras/${sampleId}/resumo-normas`,
  alarmLevelParams: (assetModuleId, sampleId) => `/engenharia/${
    assetModuleId}/cromatografia/amostras/${sampleId}/resumo-gases-taxas`,
};

/* istanbul ignore next */
const error = {
  ...msis127ErrorHandler,
};

/**
 * Dados de resultado de uma norma.
 * @typedef RuleResultData
 * @property {String} method Método, enum.
 * @property {Boolean} alarmed Sinaliza se o método está alarmado.
 * @property {Boolean} executed Sinaliza se o método foi executado ou não executado.
 * @property {any|NotExecutedData} data Dados específicos do método que serão usados no respectivo
 *  card. Os dados do método são específicos de cada caso, porém, se `executed` for igual
 *  a `false`, então o retorno em `data` deverá ser no formato `NotExecutedData`.
 */

/**
 * Dados de um método não executado.
 * @typedef NotExecutedData
 * @property {Number} multiplier Parametro "multiplicador".
 * @property {Number} percentage Parametro de percentagem do valor.
 * @property {Array} gas Array dos gases e seus L.A.A.s.
 * @property {String} gas[].gas Nome do gas.
 * @property {Number} gas[].aal Valor do L.A.A. do gas.
 */

/**
 * Retorna o lookup da amostras.
 * @param {String} assetModuleId Id do módulo ativo.
 * @param {String[]} sampleIds Ids das amostras.
 * @returns {{ id: String, number: Number }[]}
 */
const getSampleList = (assetModuleId, sampleIds) => new Http(error)
  .post(urls.sampleLookup(assetModuleId), { ids: sampleIds })
  .then(({ data }) => data.map(item => new SampleLookup(item)))
  .catch(error.msis127ErrorHandler);

/**
 * Retorna o resumo da amostra, informando quais normas estão ou não alarmadas.
 * @param {String} assetModuleId Id do módulo ativo.
 * @param {String} sampleId Id da amostra.
 * @returns {{ id: String, number: Number, summary: { rule: String, alarmed: Boolean }[] }}
 */
const getSampleSummary = (assetModuleId, sampleId) => new Http(error)
  .get(urls.sampleSummary(assetModuleId, sampleId))
  .then(({ data }) => new SampleSummary(data))
  .catch(error.msis127ErrorHandler);

/**
 * Retorna os dados/resultado de uma norma.
 * @param {String} assetModuleId Id do módulo ativo.
 * @param {String} sampleId Id da amostra.
 * @param {String} rule Norma.
 * @returns {Promise<RuleResultData[]>} Array de resultados por método.
 */
const getRuleResult = (assetModuleId, sampleId, rule) => {
  switch (rule) {
    case Rules.IEC_60599:
      return ruleResults.getIec60599Result(assetModuleId, sampleId);
    case Rules.NBR_7274:
      return ruleResults.getNbr7274Result(assetModuleId, sampleId);
    case Rules.IEEE_C57_104:
      return ruleResults.getIeeeC57104Result(assetModuleId, sampleId);
    case Rules.DUVAL:
      return ruleResults.getDuvalResult(assetModuleId, sampleId);
    default:
      throw new Error(`Not supported rule: "${rule}"`);
  }
};

/**
 * Retorna os dados de parametros de nível de alarmes.
 * @param {String} assetModuleId Id do módulo ativo.
 * @param {String} sampleId Id da amostra atual.
 * @returns {any}
 */
const getAlarmLevelParams = (assetModuleId, sampleId) => new Http(error)
  .get(urls.alarmLevelParams(assetModuleId, sampleId))
  .then(({ data }) => new AlarmLevelParam(data))
  .catch(error.msis127ErrorHandler);

export default {
  getRuleResult,
  getSampleSummary,
  getSampleList,
  getAlarmLevelParams,
};
